<template>
          <section class="seller-products-section py-3">

          <div class="container">
    <div class="section-heading d-flex align-items-center justify-content-between mb-3">
      <h6 class="mb-0">
        <i :class="iconClass"></i>
        {{ title }}
      </h6>
      <NuxtLink class="btn btn p-0" :to="viewAllLink">
        {{ $t('productList.view_all') }}
        <i class="fa-solid fa-arrow-left ms-1"></i>
      </NuxtLink>
    </div>
    
    <div class="product-list-container">
      <div 
        class="row g-2" 
        :class="{ 'rtl-flex-d-row-r': sectionType === 'right-to-left' }"
      >
        <div 
          class="col-6 col-md-4 col-lg-3" 
          v-for="product in products" 
          :key="product.id" 
          :class="colClass"
        >
            <div class="card product-card">
              <div class="card-body">
                <div v-if="hasBadges(product)" class="badge-list">
                  <span class="badge badge-success" v-if="product.instant">{{ $t('home.badge_instant') }}</span>
                  <span class="badge badge-warning" v-if="product.special">{{ $t('home.badge_special') }}</span>
                  <span class="badge badge-danger" v-if="product.hot" >{{ $t('market.hot') }}</span>
                  <span class="badge badge-warning" v-if="isProductOutOfSeason(product)">{{ $t('home.badge_presale') }}</span>
                  <span v-if="isFreshProduct(product)" class="badge badge-fresh">{{ $t('market.fresh') }}</span>
                  <span v-if="product.adv?.zara" class="badge badge-zara"><i class="fa-solid fa-seedling"></i> زرعا</span>


                </div>
                <NuxtLink class="product-thumbnail d-block" :to="localePath(`/details/${product.invoiceNum}`)"  target="_blank" >
                  <img class="lazyloaded" 
                  :src="`https://sarvban.com/uploads/market/${product.userId}/${product?.image}` || `${baseUrl}img/products/default.jpg`" 
                  :alt="displayProductTitle(product)"
                  @error="handleImageError">

                  <!-- <NuxtImg 
                    :src="currentHeroSrc"
                    :alt="displayProductTitle(product)"
                    @error="handleImageError"
                  /> -->
                </NuxtLink>
                <NuxtLink class="product-title" :to="localePath(`/details/${product.invoiceNum}`)"  target="_blank" >{{ displayProductTitle(product) }}</NuxtLink>
                <p class="sale-price">{{ product.username }} | {{ product?.location?.shahrestan?.label ?? '' }}</p>
                <!-- <div class="product-rating">
                  <i class="fa-solid fa-star"></i>
                  <i class="fa-solid fa-star"></i>
                  <i class="fa-solid fa-star"></i>
                  <i class="fa-solid fa-star"></i>
                  <i class="fa-solid fa-star"></i>
                </div> -->
                <!-- <div class="product-price-footer">
                  <span>{{ formatPrice(product.price.price) }}</span>
                  <small>{{ product.price.unit }}</small>
                </div> -->
                <NuxtLink class="btn btn-success " :to="localePath(`/details/${product.invoiceNum}`)" target="_blank">
                  <i class="fa-solid fa-phone me-1"></i>
                </NuxtLink>
              </div>
            </div>
        </div>
      </div>
    </div>
  </div>
  </section>
</template>

<script setup>
const baseURL = useRuntimeConfig().app.baseURL
const siteUrl = useRuntimeConfig().public.baseUrl
const localePath = useLocalePath()

// Props
const props = defineProps({
  sectionType: {
    type: String,
    default: 'left-to-right'
  },
  products: {
    type: Array,
    default: () => []
  },
  showLoadMore: {
    type: Boolean,
    default: false
  },
  viewAllLink: {
    type: String,
    default: '/market/products/sellers'
  },
  icon: {
    type: String,
    default: 'fa-solid fa-store'
  },
  title: {
    type: String,
    default: ''
  },
  colClass: {
    type: String,
    default: 'col-6'
  },
  badgeText: {
    type: String,
    default: ''
  },
  showRating: {
    type: Boolean,
    default: true
  }
})
function handleImageError(event) {
  if (event.target) {
    event.target.src = `${baseURL}/img/products/default.jpg`
  }
}

const imgSrc = (img, thumb = false) => {
  if (!img) return `${siteUrl}/img/products/default.jpg`
  const src = typeof img === 'string' ? img : (img.url || img.path || '')
  if (!src) return `${siteUrl}/img/products/default.jpg`
  if (src.startsWith('/') || src.startsWith('http')) return src
  if (thumb && isVideoFile(img)) return `${siteUrl}/img/default-video.jpg`
  return `https://sarvban.com/uploads/market/${src}`
}

const isVideoFile = (img) => {
  if (!img) return false
  const src = typeof img === 'string' ? img : (img.url || img.path || '')
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(src)
}

const currentHeroSrc = ref('')
// Methods
const formatPrice = (price) => {
  if (typeof price === 'number') {
    return new Intl.NumberFormat('fa-IR').format(price)
  }
  if (price && typeof price.price === 'number') {
    return new Intl.NumberFormat('fa-IR').format(price.price)
  }
  return '0'
}

const isProductOutOfSeason = (product) => {
  if (!product) return false
  const adType = product.adType || product.ad_type || ''
  return adType.toLowerCase().includes('outofseasion')
}

const displayProductTitle = (product) => {
  if (!product?.title) return ''
  return isProductOutOfSeason(product) ? `پیش ${product.title}` : product.title
}

const linkToProduct = (product) => {
  if (product.invoiceNum) {
    return localePath(`/details/${product.invoiceNum}`)
  }
  if (product.id) {
    return localePath(`/details/${product.id}`)
  }
  return '#'
}

// Computed
const iconClass = computed(() => {
  if (props.icon.includes('store')) {
    return 'text-success me-2'
  } else if (props.icon.includes('calendar')) {
    return 'text-warning me-2'
  } else if (props.icon.includes('heart')) {
    return 'text-danger me-2'
  } else if (props.icon.includes('comment')) {
    return 'text-primary me-2'
  }
  return 'me-2'
})

const badgeClass = computed(() => {
  if (props.badgeText.includes('داغ')) {
    return 'badge badge-danger'
  } else if (props.badgeText.includes('ویژه')) {
    return 'badge badge-warning'
  } else if (props.badgeText.includes('فوری')) {
    return 'badge badge-success'
  } else if (props.badgeText.includes('پیش فروش')) {
    return 'badge badge-danger'
  }
  return 'badge badge-secondary'
})


function isFreshProduct(product) {
  const raw = product.updatedAt_z || ''
  if (!raw) return false
  let time
  if (typeof raw === 'number') {
    time = raw < 1e12 ? raw * 1000 : raw
  } else {
    time = new Date(raw).getTime()
  }
  if (!time || Number.isNaN(time)) return false
  const diff = Date.now() - time
  return diff >= 0 && diff < 3 * 24 * 60 * 60 * 1000
}


const hasBadges = (item) =>{
  return item.instant || item.special || item.hot || item.adType === 'outofseasion' || isFreshProduct(item)
}

</script>

<style lang="scss" scoped>
@use "sass:color";

.seller-products-section {
  .product-card .btn {
    position: absolute;
    padding: 0;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    left: 1rem;
    bottom: 1rem;
    z-index: 99;
    font-size: 1rem;
    display: -webkit-box;
    display: -ms-flexbox;
      display: flex;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      -webkit-box-pack: center;
      -ms-flex-pack: center;
      justify-content: center;
    }
    .product-card .btn {
      width: 50px;
      height: 50px;
      left: 0.5rem;
      bottom: 0.5rem;
    }
    .product-card {
    border: none;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    overflow: hidden;
    min-height: 380px;
    .card-body {
      padding: 10px;
      position: relative;
      
      .badge-list{
        display: flex;
        flex-direction: row;
      }
      .badge {
        position: absolute;
        top: 20px;
        right: 20px;
        padding: 3px 12px;
        color: #fff;
        font-size: 0.65rem;
        font-weight: 700;
        border-radius: 100px;
              
        &.badge-success {
          background: linear-gradient(135deg, #28a745, $green-light);
          box-shadow: 0 4px 12px rgba(242,100,61,0.3);
        }
        
        &.badge-warning {
          background: linear-gradient(135deg, #ff5a3c, $saffron);
          box-shadow: 0 4px 12px rgba(242,100,61,0.3);
          color: #fff;
        }
      }
      
      .product-thumbnail {
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 8px;
          min-height: 215px;
          max-height: 215px;
        }
      }
      
      .product-title {
        display: block;
        text-decoration: none;
        color: #333;
        font-size: 1.1rem;
        font-weight: 600;
        margin: 8px 0 5px;
        line-height: 1.3;
        text-wrap-mode: nowrap;

        &:hover {
          color: #03a054;
        }
      }
      
      .sale-price {
        font-size: 0.9rem;
        color: #666;
        margin-bottom: 5px;
      }
      
      .product-rating {
        margin-bottom: 5px;
        
        i {
          color: $secondary;
          font-size: 0.7rem;
          margin-left: 2px;
        }
      }
      
      .product-price-footer {
        font-size: 0.85rem;
        font-weight: 700;
        color: #03a054;
        margin-bottom: 8px;
        
        small {
          font-size: 0.65rem;
          font-weight: 400;
          color: #666;
        }
      }
      
      .btn-success {
        font-size: 1.1rem;
        padding: 6px;
      }
    }
  }

  
  .badge {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    padding: 3px 8px;
    font-size: 0.7rem;
    font-weight: 600;
    
    &.badge-warning { background-color: $secondary; }
    &.badge-success { background-color: #28a745; }
    &.badge-danger { background-color: #dc3545; }
    &.badge-secondary { background-color: #6c757d; }
    &.badge-zara { background-color: #ca8a04; color: #fff; }
  }
}

// RTL adjustments
.rtl-flex-d-row-r {
  flex-direction: row-reverse;
}

// Smaller product styles for featured products
.product-card.small-product-card {
  .product-thumbnail-img {
    height: 120px;
  }
  
  .product-title {
    font-size: 0.75rem;
  }
  
  .product-seller,
  .product-location {
    font-size: 0.65rem;
  }
  
  .product-rating {
    margin: 8px 0;
    
    i {
      font-size: 10px;
    }
  }
}
</style>